import { useId, useMemo, useRef, useState } from 'react';
import { CoinsIcon, SearchIcon } from 'lucide-react';

import { cn } from '../cn';
import { DataTable, EmptyState, Panel, type DataTableHeader, type DataTableSort } from './primitives';
import { formatCredits } from '../format-credits';

/**
 * Small display pieces: avatars, fact lists, search inputs, the ledger table.
 */

// ─── Avatar ─────────────────────────────────────────────────────────────────

/**
 * The avatar palette. Hashing the name into a stable color means the same
 * person is the same color everywhere without storing anything.
 */
const AVATAR_COLORS = [
  '#6d36d6',
  '#0f766e',
  '#b45309',
  '#be185d',
  '#1d4ed8',
  '#4d7c0f',
] as const;

export interface AvatarProps {
  readonly name: string;
  /** Diameter in pixels. The sidebar uses 33; lists read best at 28. */
  readonly size?: number;
  /** Profile picture URL. Falls back to initials when absent. */
  readonly imageUrl?: string | null | undefined;
}

/**
 * A person's avatar: their profile picture when they have one, otherwise
 * initials on a color derived from the name.
 *
 * @param props - The person's display name, optional size and picture.
 * @returns The avatar. Decorative — pair it with visible text.
 */
export function Avatar({ name, size = 28, imageUrl }: AvatarProps): React.ReactElement {
  // Stored avatar URLs can be dead or access-denied (legacy records, per-role
  // attachment visibility) — fall back to initials instead of a broken image.
  const [imageFailed, setImageFailed] = useState(false);

  if (imageUrl && !imageFailed) {
    return (
      <img
        aria-hidden="true"
        src={imageUrl}
        alt=""
        onError={() => setImageFailed(true)}
        className="shrink-0 rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }

  const initials =
    name
      .split(' ')
      .map((part) => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';

  let hash = 0;
  for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  const color = AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];

  return (
    <span
      aria-hidden="true"
      className="grid shrink-0 place-items-center rounded-full font-bold text-white"
      style={{
        width: size,
        height: size,
        fontSize: Math.max(9, Math.round(size / 3.2)),
        background: color,
      }}
    >
      {initials}
    </span>
  );
}

// ─── Fact list ──────────────────────────────────────────────────────────────

export interface Fact {
  readonly label: string;
  readonly value: React.ReactNode;
}

export interface FactListProps {
  readonly facts: readonly Fact[];
  /** Columns at desktop width. Defaults to 3. */
  readonly columns?: 2 | 3 | 4;
  /**
   * Columns on a phone. Defaults to 1; pass 2 for short values (names, dates,
   * counts) so a long list doesn't become a single tall column.
   */
  readonly phoneColumns?: 1 | 2;
  /**
   * Show facts with no value instead of hiding them, rendering a muted "Not
   * set" in place of the value. Use on a record's canonical detail block, where
   * a blank field is information ("we don't have this yet") rather than noise.
   */
  readonly showEmpty?: boolean;
}

/** Whether a fact carries a real value. */
function hasValue(fact: Fact): boolean {
  return fact.value !== null && fact.value !== undefined && fact.value !== '';
}

/**
 * Labelled facts in a grid — the "key details" block of a detail screen.
 *
 * A real `<dl>`, so the label→value pairing survives into the accessibility
 * tree. By default facts with empty values are skipped rather than rendered as
 * em-dashes. Pass `showEmpty` on a record's canonical detail block to instead
 * render missing fields with a muted "Not set", so a reader can see what the
 * record is missing.
 *
 * @param props - The facts, an optional column count, and `showEmpty`.
 * @returns The definition list.
 */
export function FactList({ facts, columns = 3, phoneColumns = 1, showEmpty = false }: FactListProps): React.ReactElement {
  const shown = showEmpty ? facts : facts.filter(hasValue);

  return (
    <dl
      className={cn(
        'grid gap-x-6 gap-y-4',
        phoneColumns === 2 && 'grid-cols-2',
        columns === 2 && 'sm:grid-cols-2',
        columns === 3 && 'sm:grid-cols-2 lg:grid-cols-3',
        columns === 4 && 'sm:grid-cols-2 lg:grid-cols-4',
      )}
    >
      {shown.map((fact) => (
        <div key={fact.label} className="min-w-0">
          <dt className="text-portal-micro font-extrabold tracking-[0.6px] text-portal-muted uppercase">
            {fact.label}
          </dt>
          <dd className="mt-1 break-words text-portal-body">
            {hasValue(fact) ? (
              fact.value
            ) : (
              <span className="text-portal-muted/70 italic">Not set</span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

// ─── Search input ───────────────────────────────────────────────────────────

export interface SearchInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className' | 'type'> {
  /** Names the field for screen readers; there is no visible label. */
  readonly label: string;
  readonly className?: string;
}

/**
 * A search field with the magnifier affordance.
 *
 * @param props - Native input props plus the accessible label.
 * @returns The input.
 */
export function SearchInput({ label, className, ...rest }: SearchInputProps): React.ReactElement {
  return (
    <span className={cn('relative block', className)}>
      <SearchIcon
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-portal-muted"
      />
      <input
        type="search"
        aria-label={label}
        className="h-[43px] w-full rounded-portal-input border border-[#cad3df] bg-white pr-3 pl-8 text-[16px] min-[720px]:text-portal-small"
        {...rest}
      />
    </span>
  );
}

// ─── Ledger table ───────────────────────────────────────────────────────────

/** One credit movement, pre-shaped by the caller's own labels and context. */
export interface LedgerTableRow {
  readonly id: string;
  /** ISO timestamp of the movement. */
  readonly date: string;
  /** The activity, already humanized — "Allocated in", "Credits purchased". */
  readonly label: string;
  /** Muted lines under the label: the reason, the booking, the actor. */
  readonly details?: readonly string[];
  /** Which pool the row belongs to. Omit everywhere to drop the column. */
  readonly pool?: string | null;
  /** Signed credits: positive adds to the account, negative removes. */
  readonly amount: number;
}

/** The ledger columns that sort, named once for callers wiring `useTableSort`. */
export const LEDGER_SORT_KEYS = { date: 'date', credits: 'credits' } as const;

export interface LedgerTableProps {
  readonly rows: readonly LedgerTableRow[];
  /** Shown inside an empty panel when there are no rows. */
  readonly emptyMessage?: string;
  /** The active sort, when the caller orders the rows (Date and Credits sort). */
  readonly sort?: DataTableSort | undefined;
  readonly onSort?: ((key: string) => void) | undefined;
}

/**
 * The credit transaction history — the same table wherever a ledger appears,
 * client portal or BCE admin. Every row names who caused it and why, because
 * "why does this say 26" is the first question anyone asks of a balance.
 *
 * The Pool column renders only when at least one row names a pool, so a
 * single-account ledger doesn't carry a column of repetition. Sorting is the
 * caller's: pass `sort` and `onSort` and the Date and Credits headers sort.
 *
 * @param props - Pre-shaped rows, the empty-state copy and the optional sort.
 * @returns The ledger table.
 */
export function LedgerTable({
  rows,
  emptyMessage = 'No credit activity yet.',
  sort,
  onSort,
}: LedgerTableProps): React.ReactElement {
  if (rows.length === 0) {
    return (
      <Panel>
        <EmptyState icon={<CoinsIcon aria-hidden className="size-6" />} title="No credit activity" message={emptyMessage} />
      </Panel>
    );
  }

  const showPool = rows.some((row) => row.pool != null && row.pool !== '');
  const sortable = onSort !== undefined;
  const date: DataTableHeader = sortable ? { label: 'Date', sortKey: LEDGER_SORT_KEYS.date } : 'Date';
  const credits: DataTableHeader = sortable ? { label: 'Credits', sortKey: LEDGER_SORT_KEYS.credits } : 'Credits';
  const headers: readonly DataTableHeader[] = showPool
    ? [date, 'Activity', 'Pool', credits]
    : [date, 'Activity', credits];

  return (
    <DataTable
      headers={headers}
      rightAlignedHeaders={[headers.length - 1]}
      {...(sortable ? { onSort, ...(sort === undefined ? {} : { sort }) } : {})}
    >
      {rows.map((row) => (
        <tr key={row.id} className="border-t border-portal-line align-middle">
          <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">
            {new Date(row.date).toLocaleDateString('en-US', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </td>
          <td className="p-3 text-portal-small">
            <b>{row.label}</b>
            {(row.details ?? []).map((detail) => (
              <span key={detail} className="block text-portal-micro text-portal-muted">
                {detail}
              </span>
            ))}
          </td>
          {showPool ? <td className="p-3 text-portal-small text-portal-muted">{row.pool}</td> : null}
          <td
            className={cn(
              'p-3 text-right text-portal-small font-bold whitespace-nowrap tabular-nums',
              row.amount >= 0 ? 'text-portal-success' : 'text-portal-ink',
            )}
          >
            {row.amount >= 0 ? '+' : ''}
            {formatCredits(row.amount)}
          </td>
        </tr>
      ))}
    </DataTable>
  );
}

// ─── Combobox ───────────────────────────────────────────────────────────────

/** One choosable entry. */
export interface ComboboxOption {
  readonly value: string;
  readonly label: string;
}

export interface ComboboxProps {
  readonly options: readonly ComboboxOption[];
  /** The selected option's value, or empty string for none. */
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Shown for the empty selection, in the list and as the cleared state. */
  readonly noneLabel?: string;
  readonly placeholder?: string;
  readonly id?: string;
  readonly disabled?: boolean;
  /** Anchors a tour step: rendered as `data-guide` on the root element. */
  readonly guide?: string | undefined;
  /**
   * An entry at the foot of the list for when the right option is not there,
   * such as adding a new person. It gets what was typed, so the new record can
   * start from it.
   */
  readonly createOption?: ComboboxCreateOption | undefined;
}

/** The "not in the list" entry of a {@link Combobox}. */
export interface ComboboxCreateOption {
  /** Its label, given what was typed: `Add “Dana” as a new person`. */
  readonly label: (typed: string) => string;
  readonly onSelect: (typed: string) => void;
}

/**
 * A searchable single-select: type to filter, pick with mouse or arrows+Enter.
 *
 * The standard control wherever a native `<select>` would hold more options
 * than a glance can scan (people pickers, program pickers). Selection state is
 * the option's value; the input shows the selected label until the person
 * starts typing a filter.
 *
 * @param props - Options, selection, and change handler.
 * @returns The combobox.
 */
export function Combobox({
  options,
  value,
  onChange,
  noneLabel,
  placeholder = 'Type to search…',
  id,
  disabled = false,
  guide,
  createOption,
}: ComboboxProps): React.ReactElement {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  // When the caller opts into a `noneLabel`, an empty value is a real "none"
  // selection (e.g. "See Facilitator"), so show that label rather than the
  // search placeholder. Callers without a noneLabel keep the placeholder.
  const selected =
    options.find((option) => option.value === value) ??
    (value === '' && noneLabel != null ? { value: '', label: noneLabel } : null);

  const shown = useMemo(() => {
    const needle = (query ?? '').trim().toLowerCase();
    const matches =
      needle.length === 0
        ? options
        : options.filter((option) => option.label.toLowerCase().includes(needle));
    // The clear entry is always offered first, so "no backup" is one keystroke
    // away rather than a delete-the-text puzzle.
    return [{ value: '', label: noneLabel ?? 'None' }, ...matches];
  }, [options, query, noneLabel]);

  /** Commits an option and closes. */
  function choose(option: ComboboxOption): void {
    onChange(option.value);
    setQuery(null);
    setOpen(false);
  }

  // The create entry sits after the options, one past the last index.
  const createIndex = createOption ? shown.length : -1;
  const lastIndex = createOption ? shown.length : shown.length - 1;
  const typed = (query ?? '').trim();

  /** Hands what was typed to the create entry and closes. */
  function create(): void {
    if (!createOption) return;
    createOption.onSelect(typed);
    setQuery(null);
    setOpen(false);
  }

  return (
    <div data-guide={guide}
      ref={rootRef}
      className="relative"
      onBlur={(event) => {
        // Only close when focus leaves the whole control — clicking an option
        // blurs the input first, and closing then would swallow the click.
        if (!rootRef.current?.contains(event.relatedTarget as Node | null)) {
          setQuery(null);
          setOpen(false);
        }
      }}
    >
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        disabled={disabled}
        placeholder={placeholder}
        value={query ?? selected?.label ?? ''}
        onFocus={(event) => {
          // Select the shown label so typing replaces it rather than appending.
          event.currentTarget.select();
          setOpen(true);
          setActiveIndex(0);
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          setActiveIndex(0);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
            setActiveIndex((index) => Math.min(index + 1, lastIndex));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((index) => Math.max(index - 1, 0));
          } else if (event.key === 'Enter') {
            if (open && activeIndex === createIndex) {
              event.preventDefault();
              create();
            } else if (open && shown[activeIndex]) {
              event.preventDefault();
              choose(shown[activeIndex]);
            }
          } else if (event.key === 'Escape') {
            setQuery(null);
            setOpen(false);
          }
        }}
        className="mt-1.5 h-[43px] w-full rounded-portal-input border border-[#cad3df] bg-white px-3 text-[16px] min-[720px]:text-portal-small"
      />
      {open ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-portal-card border border-portal-line bg-white shadow-portal-raised"
        >
          {shown.map((option, index) => (
            <li key={option.value === '' ? '::none' : option.value}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => choose(option)}
                className={cn(
                  'block w-full px-3 py-2.5 text-left text-portal-small',
                  index === activeIndex && 'bg-portal-sky',
                  option.value === value && 'font-bold',
                  option.value === '' && 'text-portal-muted',
                )}
              >
                {option.label}
              </button>
            </li>
          ))}
          {shown.length === 1 ? (
            <li className="px-3 py-2.5 text-portal-micro text-portal-muted">No matches.</li>
          ) : null}
          {createOption ? (
            <li className="border-t border-portal-line">
              <button
                type="button"
                role="option"
                aria-selected={false}
                onMouseEnter={() => setActiveIndex(createIndex)}
                onClick={create}
                className={cn('block w-full px-3 py-2.5 text-left text-portal-small font-bold text-brand', activeIndex === createIndex && 'bg-portal-sky')}
              >
                {createOption.label(typed)}
              </button>
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

export interface MultiComboboxProps {
  readonly options: readonly ComboboxOption[];
  /** The selected options' values, in the order they were picked. */
  readonly values: readonly string[];
  readonly onChange: (values: readonly string[]) => void;
  readonly placeholder?: string;
  readonly id?: string;
  readonly disabled?: boolean;
  /**
   * Lets a value that is not in the list be added from what was typed, as
   * free tags are. The list still offers what is in use first, so the
   * vocabulary does not split into near-duplicates.
   */
  readonly allowNew?: boolean;
}

/**
 * A searchable pick-many: type to filter, pick with mouse or arrows+Enter,
 * each pick becomes a chip with its own remove button. Backspace on an
 * empty filter removes the last chip.
 *
 * The standard control wherever several of a long list are chosen (teams,
 * programs, members). A row of checkboxes stops working past a dozen.
 *
 * @param props - Options, selection, and change handler.
 * @returns The control.
 */
export function MultiCombobox({
  options,
  values,
  onChange,
  placeholder = 'Type to search…',
  id,
  disabled = false,
  allowNew = false,
}: MultiComboboxProps): React.ReactElement {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const byValue = useMemo(() => new Map(options.map((option) => [option.value, option])), [options]);
  const chosen = values
    .map((value) => byValue.get(value) ?? (allowNew ? { value, label: value } : undefined))
    .filter((option): option is ComboboxOption => option !== undefined);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matching = needle.length === 0 ? options : options.filter((option) => option.label.toLowerCase().includes(needle));
    const known = options.some((option) => option.label.toLowerCase() === needle) || values.includes(needle);
    // Typed something new: offer it as its own value, after what is in use.
    return allowNew && needle.length > 0 && !known
      ? [...matching, { value: needle, label: `Add “${query.trim()}”` }]
      : matching;
  }, [options, query, allowNew, values]);

  function toggle(option: ComboboxOption): void {
    onChange(values.includes(option.value) ? values.filter((value) => value !== option.value) : [...values, option.value]);
    setQuery('');
    inputRef.current?.focus();
  }

  function remove(value: string): void {
    onChange(values.filter((candidate) => candidate !== value));
  }

  return (
    <div
      ref={rootRef}
      className="relative"
      onBlur={(event) => {
        if (!rootRef.current?.contains(event.relatedTarget as Node | null)) {
          setQuery('');
          setOpen(false);
        }
      }}
    >
      <div
        className={cn(
          'mt-1.5 flex min-h-[43px] w-full flex-wrap items-center gap-1.5 rounded-portal-input border border-[#cad3df] bg-white px-2 py-1',
          disabled && 'opacity-60',
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {chosen.map((option) => (
          <span
            key={option.value}
            className="inline-flex items-center gap-1 rounded-portal-pill bg-brand-tint py-0.5 pl-2 pr-1 text-portal-micro font-bold text-brand"
          >
            {option.label}
            <button
              type="button"
              aria-label={`Remove ${option.label}`}
              disabled={disabled}
              onClick={(event) => {
                event.stopPropagation();
                remove(option.value);
              }}
              className="grid size-4 place-items-center rounded-full hover:bg-white/70"
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          disabled={disabled}
          placeholder={chosen.length === 0 ? placeholder : ''}
          value={query}
          onFocus={() => {
            setOpen(true);
            setActiveIndex(0);
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            setActiveIndex(0);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
              setActiveIndex((index) => Math.min(index + 1, shown.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActiveIndex((index) => Math.max(index - 1, 0));
            } else if (event.key === 'Enter') {
              if (open && shown[activeIndex]) {
                event.preventDefault();
                toggle(shown[activeIndex]);
              }
            } else if (event.key === 'Backspace' && query.length === 0 && values.length > 0) {
              remove(values[values.length - 1] as string);
            } else if (event.key === 'Escape') {
              setQuery('');
              setOpen(false);
            }
          }}
          className="h-8 min-w-[8rem] flex-1 bg-transparent px-1 text-[16px] outline-none min-[720px]:text-portal-small"
        />
      </div>
      {open ? (
        <ul
          id={listId}
          role="listbox"
          aria-multiselectable
          className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-portal-card border border-portal-line bg-white shadow-portal-raised"
        >
          {shown.map((option, index) => {
            const picked = values.includes(option.value);
            return (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={picked}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => toggle(option)}
                  className={cn(
                    'flex w-full items-center justify-between px-3 py-2.5 text-left text-portal-small',
                    index === activeIndex && 'bg-portal-sky',
                    picked && 'font-bold',
                  )}
                >
                  {option.label}
                  {picked ? <span aria-hidden className="text-brand">✓</span> : null}
                </button>
              </li>
            );
          })}
          {shown.length === 0 ? (
            <li className="px-3 py-2.5 text-portal-micro text-portal-muted">No matches.</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
